'use client';
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from 'react';
import NewsPreview from '../NewsPreview';
import ShimmerNewsList from './FeedShimmer';
import { getNewsFeed } from '@/actions/news';
import { FeedType } from '@/lib/enums';
import type { LocationObject, NewsCardMeta } from '@/lib/types';
import type { FeedPreviewCard } from '@/lib/feed_preview';
import { useInView } from 'react-intersection-observer';
import NearbyLocationBar from '../NearbyLocationBar';
import AdComponent from '../AdComponent';
import AdFullPage from '../AdComponent/AdFullPage';
import PromotedNewsItem from '../PromotedNewsItem';
import { getPromotedVideoConfig } from '@/constants/promoted_videos';
// Removed unused FeedItem interface

interface InfiniteFeedProps {
  /** Slimmed on the server (lib/feed_preview.ts) — only what NewsPreview renders. */
  initialFeed?: FeedPreviewCard[];
  offset?: null | string;
  feedType: FeedType;
  tagId?: string; // Optional, used when feedType is 'tag'
  location_code?: string | null; // Optional, used when feedType is 'generic'
  nearbyLocations?: any; // Optional, used for nearby locations
  secondNearbyLocations?: any; // Optional, used for second nearby locations
  /** From server when initialFeed was loaded on SSR (e.g. video page). */
  initialHasMore?: boolean;
  /** Currently-playing video id; used to opt-in to promoted creatives. */
  selectedVideoId?: string | null;
}

// An ad break shown on small screens only. The box is always in the HTML
// (hidden by CSS from 800px up) with the ad's height reserved, so nothing
// moves when the ad mounts. The ad itself mounts only where the box is
// actually visible: AdComponent requests an ad even inside a display:none
// parent, so rendering it unconditionally would fire hidden ad requests on
// desktop.
const MobileOnlyAd: React.FC<{ index: number }> = ({ index }) => {
  const [enabled, setEnabled] = useState(false);
  useLayoutEffect(() => {
    const mq = window.matchMedia('(max-width: 799px)');
    if (mq.matches) setEnabled(true);
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setEnabled(true);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return (
    <div className='col-span-full min-[800px]:hidden'>
      {enabled ? (
        <AdComponent index={index} identifier={'Website_300*250'} />
      ) : (
        <div style={{ minHeight: 250, margin: '16px 0px' }} />
      )}
    </div>
  );
};

const InfiniteFeed: React.FC<InfiniteFeedProps> = ({
  initialFeed,
  feedType,
  offset: initialOffset,
  location_code,
  tagId,
  nearbyLocations,
  secondNearbyLocations,
  initialHasMore,
  selectedVideoId,
}) => {
  const promotedConfig = getPromotedVideoConfig(selectedVideoId);
  const canFetchFeed =
    feedType === FeedType.TAG
      ? !!tagId
      : (feedType === FeedType.CITY ||
            feedType === FeedType.STATE ||
            feedType === FeedType.DISTRICT) &&
          !!location_code;

  const [feedItems, setFeedItems] = useState<FeedPreviewCard[]>(initialFeed || []);
  const [offset, setOffset] = useState(initialOffset || null);
  const [loading, setLoading] = useState(() => {
    if (initialFeed && initialFeed.length > 0) return false;
    if (!canFetchFeed) return false;
    // Video page (etc.): server already resolved the first page — no client spinner.
    if (typeof initialHasMore === 'boolean') return false;
    return true;
  });
  const [hasMore, setHasMore] = useState(() =>
    typeof initialHasMore === 'boolean' ? initialHasMore : canFetchFeed,
  );
  const feedIdSet = useRef<Set<string>>(new Set());

  const fetchFeedItems = useCallback(async () => {
    if (loading) return; // Prevent redundant calls

    if (!hasMore) {
      console.log('No more items to load.');
      return;
    }
    setLoading(true);
    let newFeed = null;
    switch (feedType) {
      case FeedType.TAG:
        newFeed = await getNewsFeed({
          type: feedType,
          feedId: tagId as string,
          offset,
          maxCards: 5,
        });
        break;
      case FeedType.CITY:
      case FeedType.STATE:
      case FeedType.DISTRICT:
        newFeed = await getNewsFeed({
          type: feedType,
          feedId: location_code as string,
          offset,
          maxCards: 5,
        });
        break;
      default:
        break;
    }

    if (!newFeed || 'error' in newFeed || !newFeed.card_list) {
      console.error('Error fetching feed items:', newFeed);
      setLoading(false);
      setHasMore(false);
      return;
    }

    const uniqueCards = newFeed.card_list.filter(
      (item: NewsCardMeta) => !feedIdSet.current.has(item.id),
    );
    uniqueCards.forEach((item: NewsCardMeta) => feedIdSet.current.add(item.id));

    if (uniqueCards.length === 0) {
      console.log('No new feed items to add.');
      setOffset(newFeed.min_card_id || null);
      setLoading(false);
      return;
    }

    setFeedItems((prev) => [...prev, ...uniqueCards]);
    setOffset(newFeed.min_card_id || null);
    setLoading(false);
    setHasMore(newFeed.reload_required || false);
  }, [loading, hasMore, feedType, tagId, location_code]); // Removed offset to prevent circular dependency

  // Start loading the next page while the end of the list is still ~1500px
  // below the viewport. Loading only once the end was on screen meant the
  // new cards pushed whatever was visible there (the footer) out of view —
  // a layout shift on every page of results (measured 0.08 each, Oct 2026).
  const { ref } = useInView({
    threshold: 0,
    rootMargin: '0px 0px 1500px 0px',
    onChange: (inView) => {
      if (inView && hasMore && !loading) {
        console.log('Loading more items...');
        fetchFeedItems();
      }
    },
  });

  // Initialize feed on mount only
  useEffect(() => {
    if (initialFeed && initialFeed.length > 0) {
      initialFeed.forEach((item) => feedIdSet.current.add(item.id));
    } else if (canFetchFeed) {
      fetchFeedItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  return (
    <div className='@container w-full lg:h-full'>
      {promotedConfig ? null : (
        <div className='hidden @max-[700px]:block'>
          <AdComponent index={0} identifier={'Website_300*250'} />
          {/* Right wala Top p */}
        </div>
      )}
      <div className='w-full lg:h-full lg:overflow-y-auto lg:no-scrollbar'>
        {promotedConfig ? (
          <div className='mb-4'>
            <PromotedNewsItem config={promotedConfig} />
          </div>
        ) : null}
        {/* One flat, append-only grid. Cards never move once rendered:
            a break (ads / nearby locations) follows every 2nd card, and
            which breaks are visible is decided by CSS, not by JS.

            This replaces slicing feedItems into chunks sized from the
            window width, which shifted layout twice (Search Console CWV,
            Oct 2026 — CLS 0.45-0.51 on city/tag/district, 0.14 on video):
            - the server rendered chunks of 3, then the browser re-chunked
              to 2 (mobile) or 4 (desktop) after hydration;
            - every "load more" re-sliced the whole list, so new cards
              landed inside an earlier chunk and pushed its ad down
              (0.36-0.40 per append).
            `grid-flow-row-dense` lets later cards backfill the row before
            a full-width break, so multi-column layouts stay unragged. */}
        <div className='grid grid-flow-row-dense @max-[600px]:grid-cols-1 @min-[1100px]:grid-cols-4 @min-[800px]:grid-cols-3 gap-4'>
          {feedItems.map((news, i) => {
            const position = i + 1;
            const breakNo = position % 2 === 0 ? position / 2 : 0;
            return (
              <React.Fragment key={news.id}>
                <NewsPreview newsObject={news} layout='horizontal' />
                {breakNo > 0 && breakNo % 2 === 1 && (
                  // After cards 2, 6, 10…: small screens only (they get an
                  // ad every 2 cards; wider layouts every 4).
                  <MobileOnlyAd index={breakNo} />
                )}
                {breakNo > 0 && breakNo % 2 === 0 && (
                  <div className='col-span-full'>
                    {breakNo === 2 && nearbyLocations ? (
                      <>
                        <AdComponent index={breakNo} identifier={'Website_300*250'} />
                        <NearbyLocationBar
                          size='large'
                          locations={
                            nearbyLocations as Record<string, LocationObject>
                          }
                          type={feedType}
                          header={
                            feedType === FeedType.DISTRICT
                              ? 'Major Districts'
                              : 'Nearby Cities'
                          }
                        />
                        <AdComponent index={breakNo + 100} identifier={'Website_300*250'} />
                      </>
                    ) : breakNo === 4 && secondNearbyLocations ? (
                      <>
                        <AdComponent index={breakNo} identifier={'Website_300*250'} />
                        <NearbyLocationBar
                          size='small'
                          locations={
                            secondNearbyLocations as Record<string, LocationObject>
                          }
                          type={FeedType.CITY}
                          header='Cities'
                        />
                        <AdComponent index={breakNo + 100} identifier={'Website_300*250'} />
                      </>
                    ) : (
                      <AdComponent index={breakNo} identifier={'Website_300*250'} />
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div
          ref={ref}
          className='w-full flex justify-center items-center mt-2 col-span-full'
        >
          {feedItems.length === 0 && loading && (
            <div className='animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary'></div>
          )}
        </div>
        {feedItems.length === 0 && loading && <ShimmerNewsList count={3} />}
        {feedItems.length === 0 && !loading && !canFetchFeed && (
          <p className='text-center text-gray-500 text-sm py-6 px-2'>
            No related stories for this location.
          </p>
        )}
        {feedItems.length === 0 &&
          !loading &&
          canFetchFeed &&
          !hasMore &&
          typeof initialHasMore === 'boolean' && (
            <p className='text-center text-gray-500 text-sm py-6 px-2'>
              No related stories right now.
            </p>
          )}
      </div>
    </div>
  );
};

export default InfiniteFeed;
